import { useQuery } from '@tanstack/react-query';
import { darkTheme } from '../../lib/tokens';
import { fetchReach } from './api';

export function ReachStatsBlock({
  announcementId,
  app,
}: {
  announcementId: string;
  app: string;
}): JSX.Element {
  const { data, isLoading, error } = useQuery({
    queryKey: ['announcement-reach', announcementId, app],
    queryFn: () => fetchReach(announcementId, app),
    refetchInterval: 30_000,
  });

  if (isLoading) {
    return (
      <div className="text-[11px] text-text-tertiary">Lädt Reach …</div>
    );
  }
  if (error || !data) {
    return (
      <div className="text-[11px] text-danger">
        Reach nicht abrufbar
      </div>
    );
  }

  const ctrPct = (data.clickThroughRate * 100).toFixed(1);

  return (
    <div className="space-y-2">
      <div className="grid grid-cols-3 gap-2">
        <Tile
          label="Impressions"
          total={data.counts.SHOWN}
          unique={data.distinctUsers.SHOWN}
        />
        <Tile
          label="Dismissed"
          total={data.counts.DISMISSED}
          unique={data.distinctUsers.DISMISSED}
        />
        <Tile
          label="CTA-Klicks"
          total={data.counts.CTA_CLICKED}
          unique={data.distinctUsers.CTA_CLICKED}
          accent
        />
      </div>
      <div className="text-[11px] text-text-tertiary">
        CTR (unique): <span className="font-mono text-text-primary">{ctrPct}%</span> ·
        aktualisiert alle 30 s
      </div>
    </div>
  );
}

function Tile({
  label,
  total,
  unique,
  accent,
}: {
  label: string;
  total: number;
  unique: number;
  accent?: boolean;
}): JSX.Element {
  return (
    <div
      className="rounded-md border p-2"
      style={{
        background: accent ? darkTheme.colors.accentSubtle : darkTheme.colors.bgBase,
        borderColor: accent ? darkTheme.colors.accent : darkTheme.colors.borderSubtle,
      }}
    >
      <div className="text-[10px] font-semibold uppercase tracking-wide text-text-tertiary">
        {label}
      </div>
      <div
        className="text-xl font-bold tabular"
        style={{ color: accent ? darkTheme.colors.accentLight : darkTheme.colors.textPrimary }}
      >
        {total.toLocaleString('de-DE')}
      </div>
      <div className="text-[10px] text-text-tertiary">
        {unique.toLocaleString('de-DE')} unique
      </div>
    </div>
  );
}

// Inline-Variante für die Listen-Row.
export function InlineReach({
  announcementId,
  app,
}: {
  announcementId: string;
  app: string;
}): JSX.Element {
  const { data } = useQuery({
    queryKey: ['announcement-reach', announcementId, app],
    queryFn: () => fetchReach(announcementId, app),
    staleTime: 60_000,
  });

  if (!data) return <span className="text-text-tertiary">…</span>;

  const ctrPct = (data.clickThroughRate * 100).toFixed(0);
  return (
    <span className="text-[11px] text-text-tertiary">
      <span className="text-text-secondary">{data.distinctUsers.SHOWN}</span> User ·{' '}
      <span className="text-accent-light">{ctrPct}%</span> CTR
    </span>
  );
}
